// Shared URL builders for internal links that require specific query params to
// resolve on the receiving page (e.g. ViewFeatured needs direction+ticker+window
// to look up the right cached match — see ViewFeatured.tsx). Centralizing these
// avoids each call site hand-building template literals with slightly different
// (and easy to typo) param names/sets.

interface ViewFeaturedLinkParams {
  ticker: string;
  direction: string;
  window: number | string;
  date?: string;
  // Signed per-signal unlock token (from /api/share or the earnings teaser) — forwarded
  // to /api/featured so a logged-out viewer sees this one signal past the freemium gate.
  unlock?: string;
}

export function buildViewFeaturedUrl(params: ViewFeaturedLinkParams): string {
  const qs = new URLSearchParams({
    direction: params.direction,
    ticker: params.ticker,
    window: String(params.window),
  });
  if (params.date) qs.set("date", params.date);
  if (params.unlock) qs.set("unlock", params.unlock);
  return `/view-featured?${qs.toString()}`;
}

interface DashboardLinkParams {
  ticker: string;
  window?: number | string;
  date?: string;
  score?: number | string;
  numMatches?: number | string;
  forecastWindow?: number | string;
  betaAware?: boolean;
  auto?: boolean;
}

export function buildDashboardUrl(params: DashboardLinkParams): string {
  const qs = new URLSearchParams({ ticker: params.ticker });
  if (params.window !== undefined) qs.set("window", String(params.window));
  if (params.date !== undefined) qs.set("date", params.date);
  if (params.score !== undefined) qs.set("score", String(params.score));
  if (params.numMatches !== undefined) qs.set("numMatches", String(params.numMatches));
  if (params.forecastWindow !== undefined) qs.set("forecastWindow", String(params.forecastWindow));
  if (params.betaAware) qs.set("betaAware", "1");
  if (params.auto) qs.set("auto", "true");
  return `/dashboard?${qs.toString()}`;
}

interface PatternDetailLinkParams {
  ticker: string;
  patternType: string;
  startDate: string;
  window?: number | string;
  lookbackDays?: number | string;
  sentiment?: string;
  view?: string;
  types?: string;
  // Signed per-signal unlock token (from /api/share or the earnings teaser) — forwarded
  // to /api/patterns so a logged-out viewer sees this one pattern past the freemium gate.
  unlock?: string;
}

export function buildPatternDetailUrl(params: PatternDetailLinkParams): string {
  const qs = new URLSearchParams();
  if (params.sentiment) qs.set("sentiment", params.sentiment);
  if (params.window !== undefined) qs.set("window", String(params.window));
  if (params.view) qs.set("view", params.view);
  if (params.types) qs.set("types", params.types);
  if (params.lookbackDays !== undefined) qs.set("lookback_days", String(params.lookbackDays));
  if (params.unlock) qs.set("unlock", params.unlock);
  const query = qs.toString();
  const path = `/patterns/${encodeURIComponent(params.ticker)}/${encodeURIComponent(params.patternType)}/${encodeURIComponent(params.startDate)}`;
  return query ? `${path}?${query}` : path;
}

interface OgFeaturedParams {
  ticker: string;
  window: number | string;
  direction: string;
  theme?: "light" | "dark";
}

// Public OG signal-card PNG (GET /api/og/featured/<ticker>?window=&direction=). Same-origin
// relative path — CloudFront routes /api/* to the backend; usable as an <img> src for any
// viewer (unauthenticated included), unlike the gated /api/featured payload. theme=light
// renders a light-background card for inline homepage use (default dark for social unfurls).
export function buildOgFeaturedUrl(params: OgFeaturedParams): string {
  const qs = new URLSearchParams({
    window: String(params.window),
    direction: params.direction,
  });
  if (params.theme) qs.set("theme", params.theme);
  return `/api/og/featured/${encodeURIComponent(params.ticker)}?${qs.toString()}`;
}

interface OgPatternParams {
  ticker: string;
  patternType: string;
  window?: number | string;
  startDate?: string;
  theme?: "light" | "dark";
}

// Public OG classic-pattern card PNG (GET /api/og/pattern/<ticker>?type=&window=&start=).
// Same public/relative semantics as buildOgFeaturedUrl.
export function buildOgPatternUrl(params: OgPatternParams): string {
  const qs = new URLSearchParams({ type: params.patternType });
  if (params.window !== undefined) qs.set("window", String(params.window));
  if (params.startDate) qs.set("start", params.startDate);
  if (params.theme) qs.set("theme", params.theme);
  return `/api/og/pattern/${encodeURIComponent(params.ticker)}?${qs.toString()}`;
}

/**
 * Where a locked/teased row sends an anonymous visitor.
 *
 * These CTAs pointed at /login until 2026-09-06, which was the single largest
 * conversion leak on the site: the anonymous /patterns page alone rendered 1,793
 * links to /login against 2 to /signup, every one of them labelled "Sign up to
 * reveal". Analytics matched the defect exactly — 113 people reached /login
 * against 8 successful logins, while only 15 signup attempts ever reached Cognito
 * (14 of which succeeded, so the form itself was never the problem).
 */
export const SIGNUP_URL = "/signup";
